// Fullscreen and zen-mode toggles.
//
// Fullscreen: browser Fullscreen API, requires user gesture.
// Zen:        hide all UI chrome (top/left/right/bottom panels), just canvas.
// They compose: enter both for distraction-free pixel surgery.

import { signal, type ReadSignal } from '@/core/signals';

const _fullscreen = signal<boolean>(false);
const _zen = signal<boolean>(false);

export const isFullscreen: ReadSignal<boolean> = {
  get: _fullscreen.get.bind(_fullscreen),
  peek: _fullscreen.peek.bind(_fullscreen),
};

export const isZen: ReadSignal<boolean> = {
  get: _zen.get.bind(_zen),
  peek: _zen.peek.bind(_zen),
};

let wired = false;

function wireOnce(): void {
  if (wired || typeof document === 'undefined') return;
  wired = true;
  document.addEventListener('fullscreenchange', () => {
    _fullscreen.set(document.fullscreenElement !== null);
  });
}

export function toggleFullscreen(): void {
  wireOnce();
  if (typeof document === 'undefined') return;
  if (document.fullscreenElement) {
    void document.exitFullscreen().catch(() => {});
  } else {
    void document.documentElement.requestFullscreen().catch(() => {});
  }
}

export function toggleZen(): void {
  if (typeof document === 'undefined') return;
  const next = !_zen.peek();
  _zen.set(next);
  document.body.classList.toggle('zen', next);
  if (next) showZenHint();
}

let hintTimer: ReturnType<typeof setTimeout> | null = null;
function showZenHint(): void {
  if (typeof document === 'undefined') return;
  if (hintTimer !== null) clearTimeout(hintTimer);
  let hint = document.getElementById('zen-hint');
  if (!hint) {
    hint = document.createElement('div');
    hint.id = 'zen-hint';
    hint.className = 'zen-hint mono';
    document.body.appendChild(hint);
  }
  hint.textContent = 'Zen mode  ·  press Z to exit';
  hint.classList.add('show');
  hintTimer = setTimeout(() => hint?.classList.remove('show'), 1800);
}

export function exitZen(): void {
  if (_zen.peek()) toggleZen();
}
